import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { SAVE_VERSION, type NewOutboxEntry, type OutboxEntry, type SaveData } from "./types.ts";

/**
 * Persistence boundary. React never owns the only copy of the save: every
 * change goes through SaveService into one of these stores.
 */
export interface SaveStore {
  readonly durable: boolean;
  loadSave(): Promise<SaveData | null>;
  writeSave(save: SaveData): Promise<void>;
  /** Writes the result proof and the updated save in one transaction. */
  commitResult(entry: NewOutboxEntry, save: SaveData): Promise<OutboxEntry>;
  listOutbox(): Promise<OutboxEntry[]>;
  removeOutbox(ids: readonly number[]): Promise<void>;
  putOutbox(entries: readonly OutboxEntry[]): Promise<void>;
  /** Keeps a copy before a restore replaces the local journey. */
  backup(save: SaveData, outbox: readonly OutboxEntry[]): Promise<void>;
  replaceAll(save: SaveData): Promise<void>;
}

interface NanoDb extends DBSchema {
  kv: { key: string; value: unknown };
  outbox: { key: number; value: OutboxEntry };
}

const DB_NAME = "nano-magical-city";
const DB_VERSION = 1;

/** Upgrades older save documents in place. Add a case per SAVE_VERSION bump. */
export function migrateSave(raw: unknown): SaveData | null {
  if (!raw || typeof raw !== "object") return null;
  let doc = raw as { version?: number } & Record<string, unknown>;
  if (doc.version === 1) {
    // v2: music became available (v1 forced it off), local stats, achievements, wishlist.
    const v1 = doc as unknown as Omit<SaveData, "version" | "stats" | "announcedAchievements" | "wishlist" | "prefs"> & {
      server: (Omit<NonNullable<SaveData["server"]>, "claimedAchievements"> & { claimedAchievements?: string[] }) | null;
    };
    doc = {
      ...v1,
      version: 2,
      settings: { ...v1.settings, music: true },
      server: v1.server ? { ...v1.server, claimedAchievements: v1.server.claimedAchievements ?? [] } : null,
      stats: { playDayCount: 0, lastPlayDay: null, bestCombo: 0 },
      announcedAchievements: [],
      wishlist: null,
      prefs: { lastRank: null, installHintDismissedAt: null },
    };
  }
  if (doc.version === SAVE_VERSION) return doc as unknown as SaveData;
  return null;
}

export class IndexedDbSaveStore implements SaveStore {
  readonly durable = true;
  private dbPromise: Promise<IDBPDatabase<NanoDb>>;

  constructor(factory?: IDBFactory) {
    if (factory) (globalThis as { indexedDB?: IDBFactory }).indexedDB = factory;
    this.dbPromise = openDB<NanoDb>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        // Versioned schema upgrades keep player data across releases.
        if (oldVersion < 1) {
          db.createObjectStore("kv");
          db.createObjectStore("outbox", { keyPath: "id", autoIncrement: true });
        }
      },
    });
  }

  async loadSave(): Promise<SaveData | null> {
    const db = await this.dbPromise;
    return migrateSave(await db.get("kv", "save"));
  }

  async writeSave(save: SaveData): Promise<void> {
    const db = await this.dbPromise;
    await db.put("kv", save, "save");
  }

  async commitResult(entry: NewOutboxEntry, save: SaveData): Promise<OutboxEntry> {
    const db = await this.dbPromise;
    const tx = db.transaction(["kv", "outbox"], "readwrite");
    const id = (await tx.objectStore("outbox").add(entry as OutboxEntry)) as number;
    await tx.objectStore("kv").put(save, "save");
    await tx.done;
    return { ...entry, id };
  }

  async listOutbox(): Promise<OutboxEntry[]> {
    const db = await this.dbPromise;
    return db.getAll("outbox");
  }

  async removeOutbox(ids: readonly number[]): Promise<void> {
    const db = await this.dbPromise;
    const tx = db.transaction("outbox", "readwrite");
    await Promise.all([...ids.map((id) => tx.store.delete(id)), tx.done]);
  }

  async putOutbox(entries: readonly OutboxEntry[]): Promise<void> {
    const db = await this.dbPromise;
    const tx = db.transaction("outbox", "readwrite");
    await Promise.all([...entries.map((entry) => tx.store.put(entry)), tx.done]);
  }

  async backup(save: SaveData, outbox: readonly OutboxEntry[]): Promise<void> {
    const db = await this.dbPromise;
    await db.put("kv", { save, outbox, at: Date.now() }, "backup-before-restore");
  }

  async replaceAll(save: SaveData): Promise<void> {
    const db = await this.dbPromise;
    const tx = db.transaction(["kv", "outbox"], "readwrite");
    await tx.objectStore("outbox").clear();
    await tx.objectStore("kv").put(save, "save");
    await tx.done;
  }
}

/** Fallback when IndexedDB is unavailable (private mode): play works, nothing persists. */
export class MemorySaveStore implements SaveStore {
  readonly durable = false;
  private save: SaveData | null = null;
  private outbox = new Map<number, OutboxEntry>();
  private nextId = 1;

  async loadSave() {
    return this.save;
  }
  async writeSave(save: SaveData) {
    this.save = save;
  }
  async commitResult(entry: NewOutboxEntry, save: SaveData) {
    const stored = { ...entry, id: this.nextId++ };
    this.outbox.set(stored.id, stored);
    this.save = save;
    return stored;
  }
  async listOutbox() {
    return [...this.outbox.values()];
  }
  async removeOutbox(ids: readonly number[]) {
    for (const id of ids) this.outbox.delete(id);
  }
  async putOutbox(entries: readonly OutboxEntry[]) {
    for (const entry of entries) this.outbox.set(entry.id, entry);
  }
  async backup() {}
  async replaceAll(save: SaveData) {
    this.outbox.clear();
    this.save = save;
  }
}

export async function openSaveStore(): Promise<SaveStore> {
  try {
    if (typeof indexedDB === "undefined") throw new Error("no indexedDB");
    const store = new IndexedDbSaveStore();
    await store.loadSave();
    return store;
  } catch {
    return new MemorySaveStore();
  }
}
